import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Check, ArrowRight, Camera, Trophy, Shield, Building2, Scale, Sparkles } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { LIBRARY_IMAGE } from "@/lib/content";
import SEO from "@/components/SEO";
import PracticeSignature from "@/components/PracticeSignature";
import PracticeByline from "@/components/PracticeByline";
import { breadcrumbSchema, faqPageSchema, authoredPracticePageSchema } from "@/lib/schemas";

const AUDIENCES = [
  { icon: Camera,   label: "Digital Creators",  sub: "YouTube · TikTok · Instagram · Podcast" },
  { icon: Trophy,   label: "Professional Athletes", sub: "NFL · NBA · MLB · PGA · UFC" },
  { icon: Sparkles, label: "Recording Artists",  sub: "Country · Hip-Hop · Christian · Latin" },
  { icon: Building2, label: "Founder-Creators", sub: "D2C Brands · SaaS · Media Companies" },
];

const PILLARS = [
  {
    no: "I",
    title: "The Right Entity Architecture",
    body: "Most creators earn six or seven figures through a bank account attached to their name. Before the next brand deal, we build the layered structure that actually protects the business: operating LLC for content, brand-holding entity for IP, personal-service corp for talent income, and — when scale warrants — the family LLC that holds distributions for the long term.",
  },
  {
    no: "II",
    title: "Brand & IP Protection",
    body: "Your name, likeness, catchphrase, and channel are your enterprise. We register trademarks, structure IP-holding entities, review talent-agency and manager contracts, negotiate brand deals with FTC-compliant disclosures, and enforce against copycats — quietly, before the copycats become a market.",
  },
  {
    no: "III",
    title: "Tax Structuring — Attorney + CPA",
    body: "Darryl is dually licensed as attorney and CPA. Instead of paying 37% federal on brand-deal income, we design entity elections, retirement structures, deferred compensation, and Texas domicile advantages that a general practitioner or California attorney simply cannot deliver from a single desk.",
  },
  {
    no: "IV",
    title: "Contract Review & Negotiation",
    body: "Manager agreements. Talent agency contracts. Brand-deal terms. Podcast network agreements. Publishing deals. Endorsement contracts. We review, redline, and negotiate — with a discipline that treats each contract as a document you'll be bound by for years, not a formality to sign at the airport lounge.",
  },
  {
    no: "V",
    title: "Estate Planning While You're Young",
    body: "You are wealthy earlier than most professionals ever are. Without proper planning, a channel worth $8 million, a brand equity worth $30 million, and residuals worth $500,000 a year end up in a Texas probate court, distributed by statute to relatives who couldn't run it if you asked them to. We build the trust structures that keep your enterprise operating — and your family protected — regardless of what happens next.",
  },
  {
    no: "VI",
    title: "Discretion as a Covenant",
    body: "We do not publish client names, transactions, or matters. Your public brand is entirely separate from the private counsel supporting it. For creators whose reputation is their business, this is not a policy — it is a covenant of the engagement.",
  },
];

const COMMON_MATTERS = [
  "Creator LLC and brand-holding entity structuring",
  "Trademark registration for name, catchphrase, and channel brand",
  "Talent agency, manager, and podcast network contract review",
  "Brand deal negotiation and FTC endorsement compliance",
  "Tax structuring: entity elections, retirement, deferred comp",
  "Texas domicile establishment for out-of-state creators",
  "D2C brand equity structuring and eventual sale/exit prep",
  "Prenuptial and postnuptial agreements for high-income creators",
  "Trust structures for channel, brand equity, and residual income",
  "Estate planning for creators with minor children in content",
  "Defamation defense and litigation strategy",
  "Copyright, DMCA, and image-rights enforcement",
  "Family office set-up for creators reaching institutional scale",
  "Business succession — what happens to the enterprise if...",
];

const FAQ = [
  {
    q: "Why do I need a Texas attorney if I'm based in Los Angeles or Miami?",
    a: "Because you're not staying there. The largest creator migration in a decade is out of California and New York — and the destination is Texas. No state income tax on your brand-deal income, dramatically better asset-protection law, more favorable trust law (Texas has 300-year perpetuities), and courts that respect commercial contracts. We help creators establish genuine Texas domicile — not just a mailbox — and structure the entities that make the move legally and tax-efficiently.",
  },
  {
    q: "I already have a business manager and a CPA. Why do I need another advisor?",
    a: "You don't have another advisor — you have integrated counsel. Business managers and CPAs handle day-to-day bookkeeping, filings, and cash management. That's essential, and we don't replace it. What we handle is what they can't: entity architecture, legal contracts, trademark and IP protection, estate structures, and tax planning that requires legal drafting (trusts, entity conversions, deferred comp plans, prenups). We coordinate with your existing team — we do not displace them.",
  },
  {
    q: "How do you handle confidentiality? My brand is my public face.",
    a: "Discretion is a covenant of every creator engagement. We do not publish client names, matter types, or transactions. No case studies, no press releases, no social media mentions. If you appear in our office, no one knows. If we appear at yours, we're 'the family attorney.' For creators whose reputation is their business, this level of protection is non-negotiable.",
  },
  {
    q: "What matter types do you handle for creators and athletes?",
    a: "Entity formation and restructuring, brand and IP protection, contract review and negotiation (manager, agency, brand deal, endorsement, publishing), tax structuring, trust and estate planning, asset protection, prenuptial and postnuptial agreements, defamation defense, business succession, and — for creators who scale to genuine wealth — family office coordination and multi-generational planning. We do not handle personal injury, criminal defense, or immigration.",
  },
  {
    q: "How much do you charge, and do you take a percentage of my income like agents?",
    a: "We charge attorney fees for legal work — hourly for discrete matters, and a fixed annual retainer for standing counsel engagements. We do not take a percentage of your income, brand deals, or channel revenue. That would create conflicts of interest — and Texas Disciplinary Rules would prohibit it in most contexts anyway. Our compensation is transparent, documented in the engagement letter, and unrelated to how your enterprise performs.",
  },
];

const ROLES = [
  { key: "creator",  label: "Digital Creator (YouTube / TikTok / IG)" },
  { key: "athlete",  label: "Professional Athlete" },
  { key: "musician", label: "Recording Artist / Musician" },
  { key: "founder",  label: "Founder / D2C Brand Owner" },
  { key: "manager",  label: "Manager / Agent (referring)" },
  { key: "other",    label: "Other" },
];

const REVENUE_RANGES = [
  "Exploratory · Not yet public / pre-monetization",
  "$100,000 – $500,000 annually",
  "$500,000 – $1M annually",
  "$1M – $5M annually",
  "$5M+ annually",
  "Prefer to discuss privately",
];

export default function CreatorsAndAthletes() {
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    role: "",
    firm_name: "",
    location: "",
    estimated_value: "",
    message: "",
    preferred_contact: "email",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const canSubmit =
    form.full_name.trim() &&
    form.email.trim() &&
    form.role &&
    form.message.trim().length >= 15;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await api.post("/intake", {
        ...form,
        practice_area: "Creators & Athletes",
        stage: "protection",
        matter_class: "creator_athlete",
        referral_source: form.firm_name || null,
      });
      setDone(true);
      toast.success("Your inquiry has been received.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-ivory" data-testid="creators-athletes-page">
      <SEO
        title="Counsel for Creators & Athletes — Texas Business & Tax Attorney"
        description="Discreet Texas counsel for digital creators, professional athletes, recording artists, and founder-creators. Entity structuring, brand protection, tax, contracts, and estate planning."
        path="/practice/creators-and-athletes"
        schema={[
          ...authoredPracticePageSchema({
            path: "/practice/creators-and-athletes",
            title: "Counsel for Creators & Athletes — Texas Business & Tax Attorney",
            description: "Discreet Texas counsel for digital creators, professional athletes, recording artists, and founder-creators. Entity structuring, brand protection, tax, contracts, and estate planning.",
            discipline: "Creators & Athletes",
            minutes: 8,
            lastReviewed: "2026-02-01",
            publishedDate: "2024-11-01",
          }),
          faqPageSchema(FAQ),
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Services", path: "/practice" },
            { name: "Creators & Athletes", path: "/practice/creators-and-athletes" },
          ]),
        ]}
      />

      {/* HERO */}
      <section className="relative panel-navy min-h-[75vh] flex items-end" data-testid="ca-hero">
        <div className="absolute inset-0">
          <img src={LIBRARY_IMAGE} alt="Private library" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-b from-navy/70 via-navy/55 to-navy/95" />
        </div>
        <div className="relative max-w-[1600px] mx-auto px-6 md:px-12 py-32 md:py-40 w-full">
          <div className="kicker kicker-ivory">Counsel for Creators &amp; Athletes</div>
          <div className="mt-3 text-[11px] uppercase tracking-[0.42em] text-brass font-medium">
            Discreet <span className="text-brass/50 mx-2">·</span>
            Integrated <span className="text-brass/50 mx-2">·</span>
            Texas-Domiciled
          </div>
          <h1 className="mt-8 font-display text-ivory text-6xl md:text-8xl leading-[0.95] tracking-tight max-w-5xl">
            Your brand is <br/>
            <span className="italic font-cormorant text-brass">the enterprise.</span>
          </h1>
          <p className="mt-8 max-w-2xl font-cormorant italic text-2xl md:text-3xl text-ivory/85 leading-snug">
            A quiet Texas practice for the digital creators, professional athletes, recording
            artists, and founder-creators who take their enterprise seriously.
          </p>
        </div>
      </section>

      <PracticeByline
        minutes={8}
        lastReviewed="February 2026"
        discipline="Creators & Athletes"
      />

      <PracticeSignature
        src="/img/practice-creators-athletes.jpg"
        kicker="A Signature Detail"
        caption="A ribbon microphone, a laurel medal, a folded contract — the tools of a public life managed privately."
      />

      {/* WHO WE SERVE */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-12 py-20" data-testid="ca-audiences">
        <div className="mb-10">
          <div className="kicker">Who We Serve</div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {AUDIENCES.map((a, i) => {
            const Icon = a.icon;
            return (
              <div key={i} className="hairline-top pt-6" data-testid={`ca-audience-${i}`}>
                <div className="text-brass"><Icon size={28} strokeWidth={1.15} /></div>
                <div className="mt-5 font-display text-xl md:text-2xl text-navy leading-[1.1]">{a.label}</div>
                <div className="mt-2 font-cormorant italic text-sm text-slate2">{a.sub}</div>
              </div>
            );
          })}
        </div>
      </section>

      {/* PHILOSOPHY */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-12 py-16 md:py-24" data-testid="ca-philosophy">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="kicker">Why This Firm</div>
            <h2 className="mt-6 font-display text-4xl md:text-6xl leading-[1.0]">
              Discretion.<br/>
              <span className="italic font-cormorant text-oxblood">Integration.</span><br/>
              A quiet standard.
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 text-lg md:text-xl font-light text-slate2 leading-relaxed space-y-6">
            <p className="dropcap">
              Most creators and athletes are advised by lawyers who treat them as marketing
              opportunities — flashy case studies, press releases, LinkedIn mentions, the
              trappings of an entertainment-attorney brand. That is not this firm.
            </p>
            <p>
              We are a boutique Texas practice that treats your enterprise as what it actually
              is: a serious business earning serious income, with tax and legal implications that
              deserve the same discipline a Fortune 100 company would receive. Discretion is not
              a marketing point — it is a covenant. Your name, your matters, and your family
              circumstances remain private.
            </p>
            <p>
              And because Darryl is dually licensed as attorney and CPA, we handle the legal +
              tax analysis from a single desk — no relay between your entertainment lawyer, your
              tax attorney, and your business manager while a brand deal sits unsigned on your
              phone.
            </p>
          </div>
        </div>
      </section>

      {/* SIX PILLARS */}
      <section className="bg-linen py-24 md:py-32" data-testid="ca-pillars">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          <div className="max-w-3xl mb-14">
            <div className="kicker">The Six Pillars</div>
            <h2 className="mt-6 font-display text-5xl md:text-6xl leading-[0.98]">
              What we build<br/>
              <span className="italic font-cormorant text-oxblood">around your enterprise.</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-14">
            {PILLARS.map((p) => (
              <article key={p.no} className="hairline-top pt-6" data-testid={`ca-pillar-${p.no}`}>
                <div className="flex items-baseline gap-4">
                  <span className="font-cormorant italic text-3xl text-brass">{p.no}</span>
                  <h3 className="font-display text-2xl md:text-[1.6rem] text-navy leading-[1.15]">{p.title}</h3>
                </div>
                <p className="mt-4 text-base text-slate2 font-light leading-relaxed">{p.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* COMMON MATTERS */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-12 py-24 md:py-32" data-testid="ca-matters">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="kicker">Common Matters</div>
            <h2 className="mt-6 font-display text-4xl md:text-5xl leading-[1.02]">
              What we routinely<br/>
              <span className="italic font-cormorant text-oxblood">handle.</span>
            </h2>
          </div>
          <ul className="lg:col-span-7 lg:col-start-6 grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-3" data-testid="ca-matters-list">
            {COMMON_MATTERS.map((m, i) => (
              <li key={i} className="flex items-start gap-3 py-3 hairline-top" data-testid={`ca-matter-${i}`}>
                <Check size={15} className="text-brass shrink-0 mt-1.5" />
                <span className="font-cormorant text-base md:text-lg text-navy leading-snug">{m}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* INTELLECTUAL PROPERTY — dedicated deep block */}
      <section className="bg-linen py-24 md:py-32" data-testid="ca-ip">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          {/* Intro */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-20">
            <div className="lg:col-span-5">
              <div className="kicker">Intellectual Property</div>
              <h2 className="mt-6 font-display text-5xl md:text-6xl leading-[0.98]">
                Trademarks. Copyrights.<br/>
                <span className="italic font-cormorant text-oxblood">Brand protection.</span>
              </h2>
            </div>
            <div className="lg:col-span-6 lg:col-start-7 text-lg md:text-xl font-light text-slate2 leading-relaxed space-y-6">
              <p className="dropcap">
                For content creators, influencers, and digital entrepreneurs, intellectual property
                is often the single most valuable asset of the enterprise. Lone Star Counsel helps
                creators identify, protect, register, license, and enforce the names, brands, content,
                and creative works they have spent years building.
              </p>
              <p className="font-cormorant italic text-brass text-xl leading-snug pt-4 hairline-top">
                Your audience creates attention. Your intellectual property creates lasting business
                value. We help you protect both.
              </p>
            </div>
          </div>

          {/* Trademark & Brand Protection */}
          <div className="hairline-top pt-14 mb-16" data-testid="ca-ip-trademark">
            <div className="kicker">01 · Trademark &amp; Brand Protection</div>
            <h3 className="mt-4 font-display text-3xl md:text-4xl text-navy leading-[1.05]">
              What we protect — and how.
            </h3>
            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10">
              <div>
                <div className="text-[10px] uppercase tracking-[0.28em] text-brass font-medium">We assist with protecting</div>
                <ul className="mt-5 space-y-2 text-base font-light text-slate2 leading-snug">
                  {["Creator, influencer, and professional names",
                    "Business and agency names",
                    "Social-media account and channel names",
                    "Podcast, blog, and newsletter names",
                    "Logos, slogans, and taglines",
                    "Course, coaching-program, and membership names",
                    "Product lines, merchandise, and private-label brands",
                    "Event, conference, and community names",
                    "Mobile applications and digital-platform brands",
                  ].map((it) => (
                    <li key={it} className="flex items-baseline gap-3"><span className="text-brass mt-1 text-[0.55rem]">◆</span><span>{it}</span></li>
                  ))}
                </ul>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.28em] text-brass font-medium">Our trademark services</div>
                <ul className="mt-5 space-y-2 text-base font-light text-slate2 leading-snug">
                  {["Preliminary trademark availability reviews",
                    "Comprehensive trademark clearance searches",
                    "USPTO federal trademark applications",
                    "Texas and other state trademark applications",
                    "Responses to USPTO Office Actions",
                    "Statements of Use and extension requests",
                    "Trademark renewals and maintenance filings",
                    "Trademark assignments and ownership transfers",
                    "Trademark licensing agreements",
                    "Co-branding and collaboration agreements",
                    "Monitoring for confusingly similar brands",
                    "Cease-and-desist letters",
                    "Opposition, cancellation, and infringement strategy",
                    "Online impersonation and counterfeit-brand issues",
                  ].map((it) => (
                    <li key={it} className="flex items-baseline gap-3"><span className="text-brass mt-1 text-[0.55rem]">◆</span><span>{it}</span></li>
                  ))}
                </ul>
              </div>
            </div>
            <p className="mt-10 font-cormorant italic text-slate2/85 text-base md:text-lg leading-snug max-w-3xl">
              Federal registration through the USPTO can provide nationwide benefits and public notice
              of the owner&apos;s claim. State registration may provide an additional or more limited
              layer of protection within a particular state, depending on where and how the mark is
              being used.
            </p>
          </div>

          {/* Copyright Protection */}
          <div className="hairline-top pt-14 mb-16" data-testid="ca-ip-copyright">
            <div className="kicker">02 · Copyright Protection</div>
            <h3 className="mt-4 font-display text-3xl md:text-4xl text-navy leading-[1.05]">
              Original works, properly claimed.
            </h3>
            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10">
              <div>
                <div className="text-[10px] uppercase tracking-[0.28em] text-brass font-medium">Works we help protect</div>
                <ul className="mt-5 space-y-2 text-base font-light text-slate2 leading-snug">
                  {["Videos, reels, and livestream recordings",
                    "Photographs and digital images",
                    "Podcasts and recorded interviews",
                    "Blog posts, newsletters, and written articles",
                    "Books, e-books, and downloadable guides",
                    "Online courses and educational materials",
                    "Webinars, presentations, and training programs",
                    "Graphic designs, artwork, and illustrations",
                    "Website text and original website content",
                    "Music, sound recordings, and audiovisual works",
                    "Software, applications, and certain digital content",
                    "Advertising and promotional materials",
                  ].map((it) => (
                    <li key={it} className="flex items-baseline gap-3"><span className="text-brass mt-1 text-[0.55rem]">◆</span><span>{it}</span></li>
                  ))}
                </ul>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.28em] text-brass font-medium">Our copyright services</div>
                <ul className="mt-5 space-y-2 text-base font-light text-slate2 leading-snug">
                  {["Copyright ownership reviews",
                    "Registration with the U.S. Copyright Office",
                    "Registration strategies for works and eligible groups",
                    "Work-made-for-hire agreements",
                    "Copyright assignments and transfers",
                    "Photographer, videographer, and editor agreements",
                    "Content-development agreements",
                    "Copyright licensing and royalty agreements",
                    "Permission and release forms",
                    "DMCA takedown assistance",
                    "Responses to allegations of unauthorized use",
                    "Cease-and-desist and infringement-demand letters",
                    "Copyright policies for websites and platforms",
                    "AI-generated content and ownership considerations",
                  ].map((it) => (
                    <li key={it} className="flex items-baseline gap-3"><span className="text-brass mt-1 text-[0.55rem]">◆</span><span>{it}</span></li>
                  ))}
                </ul>
              </div>
            </div>
            <p className="mt-10 font-cormorant italic text-slate2/85 text-base md:text-lg leading-snug max-w-3xl">
              Copyright generally arises automatically when an original work is created and fixed in
              a tangible form. Registration with the U.S. Copyright Office, however, provides
              important additional enforcement and litigation benefits.
            </p>
          </div>

          {/* Ownership of Creator Content */}
          <div className="hairline-top pt-14 mb-16" data-testid="ca-ip-ownership">
            <div className="kicker">03 · Ownership of Creator Content</div>
            <h3 className="mt-4 font-display text-3xl md:text-4xl text-navy leading-[1.05]">
              The questions no one asks<br/>
              <span className="italic font-cormorant text-oxblood">until it&apos;s too late.</span>
            </h3>
            <ul className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-6">
              {["Who owns videos produced by a hired videographer?",
                "Does a social-media manager own any part of the content?",
                "Can a brand reuse sponsored content indefinitely?",
                "Can a company use the creator's name, voice, or likeness in paid advertisements?",
                "Who owns content created by an employee or independent contractor?",
                "What happens to intellectual property when a creator leaves an agency?",
                "Who owns the account, audience data, passwords, and content library?",
                "Can the creator transfer, license, or sell the brand?",
                "What happens to the content and royalty rights upon death or incapacity?",
              ].map((q, i) => (
                <li key={i} className="hairline-top pt-5 font-cormorant text-lg md:text-xl text-navy italic leading-snug">
                  <span className="text-brass font-sans not-italic text-xs mr-3 font-medium">Q{String(i + 1).padStart(2, "0")}</span>
                  {q}
                </li>
              ))}
            </ul>
          </div>

          {/* IP Licensing & Monetization */}
          <div className="hairline-top pt-14" data-testid="ca-ip-licensing">
            <div className="kicker">04 · Intellectual-Property Licensing &amp; Monetization</div>
            <h3 className="mt-4 font-display text-3xl md:text-4xl text-navy leading-[1.05]">
              Turning IP into<br/>
              <span className="italic font-cormorant text-oxblood">recurring business value.</span>
            </h3>
            <ul className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-x-10 gap-y-4">
              {["Brand licensing",
                "Merchandise licensing",
                "Content syndication",
                "Course and curriculum licensing",
                "Podcast and media licensing",
                "Name, image, and likeness agreements",
                "Affiliate and endorsement agreements",
                "Royalty and revenue-sharing arrangements",
                "Franchise and brand-expansion planning",
                "Intellectual-property holding companies",
                "Sale or transfer of creator-owned brands",
              ].map((it) => (
                <li key={it} className="flex items-baseline gap-3 hairline-top pt-4 font-cormorant text-lg text-navy leading-snug">
                  <span className="text-brass mt-1 text-[0.5rem]">★</span>
                  <span>{it}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* THE TEXAS ADVANTAGE */}
      <section className="panel-navy py-24 md:py-32" data-testid="ca-texas">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <div className="kicker kicker-ivory">The Texas Advantage</div>
              <h2 className="mt-6 font-display text-4xl md:text-5xl text-ivory leading-[1.02]">
                Why so many creators<br/>
                <span className="italic font-cormorant text-brass">are moving here.</span>
              </h2>
            </div>
            <ul className="lg:col-span-6 lg:col-start-7 space-y-6 text-ivory/85 font-light">
              {[
                "No state income tax on brand-deal, sponsorship, or channel revenue — a 10-13% delta versus California or New York on every dollar earned.",
                "300-year trust perpetuities — permitting multi-generational trust architecture that most states cannot match.",
                "Series LLC availability — allowing separately-liable series within a single filed entity for content, brand, and IP holdings.",
                "Strong homestead protection — up to 10 acres urban / 100 acres rural — that survives most creditor claims.",
                "Business-friendly courts and reasonable filing fees — commercial contracts are respected and enforced.",
                "Established Texas domicile procedure — driver's license, voter registration, principal-residence declaration — that we structure and document properly.",
              ].map((t, i) => (
                <li key={i} className="flex items-start gap-4 hairline-top pt-5" data-testid={`ca-tx-${i}`}>
                  <Check size={18} className="text-brass shrink-0 mt-1" />
                  <span className="text-base md:text-lg leading-relaxed">{t}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-[1200px] mx-auto px-6 md:px-12 py-24 md:py-32" data-testid="ca-faq">
        <div className="mb-14">
          <div className="kicker">Frequently Asked</div>
          <h2 className="mt-6 font-display text-4xl md:text-5xl leading-[1.02]">
            Questions creators<br/>
            <span className="italic font-cormorant text-oxblood">always ask first.</span>
          </h2>
        </div>
        <div className="space-y-10">
          {FAQ.map((f, i) => (
            <article key={i} className="hairline-top pt-8" data-testid={`ca-faq-${i}`}>
              <div className="text-[10px] uppercase tracking-[0.28em] text-brass font-medium">Question {String(i + 1).padStart(2, "0")}</div>
              <h3 className="mt-4 font-display text-2xl md:text-3xl text-navy leading-[1.15]">{f.q}</h3>
              <p className="mt-5 text-base md:text-lg font-light text-slate2 leading-relaxed">{f.a}</p>
            </article>
          ))}
        </div>
      </section>

      {/* INQUIRY FORM */}
      <section id="inquire" className="bg-linen py-24 md:py-32" data-testid="ca-form-section">
        <div className="max-w-[1200px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <div className="kicker">Inquire Privately</div>
              <h2 className="mt-6 font-display text-4xl md:text-5xl leading-[1.02]">
                Begin a<br/>
                <span className="italic font-cormorant text-oxblood">private conversation.</span>
              </h2>
              <p className="mt-8 text-slate2 font-light leading-relaxed">
                All inquiries are reviewed personally by Darryl V. Pratt. A response is furnished
                within two business days. Discretion is a covenant of the engagement — nothing you
                share here becomes public.
              </p>
            </div>

            {done ? (
              <div className="lg:col-span-7 lg:col-start-6" data-testid="ca-success">
                <div className="kicker">Received</div>
                <h3 className="mt-4 font-display text-4xl md:text-5xl leading-[1.02]">
                  Your inquiry has been<br/>
                  <span className="italic font-cormorant text-oxblood">received.</span>
                </h3>
                <p className="mt-8 text-lg text-slate2 font-light leading-relaxed">
                  Darryl will review your particulars personally and respond within two business
                  days. If a manager, agent, or existing counsel referred you, they will be
                  notified of the introduction only with your consent.
                </p>
                <div className="mt-10 flex flex-wrap gap-4">
                  <Link to="/" className="btn-ghost" data-testid="ca-return-home">Return Home</Link>
                  <Link to="/journal" className="btn-ghost" data-testid="ca-read-journal">Read the Journal</Link>
                </div>
              </div>
            ) : (
              <form className="lg:col-span-7 lg:col-start-6 space-y-8" onSubmit={submit} data-testid="ca-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <Field label="Full Name" required>
                    <input className="field" value={form.full_name} onChange={(e) => update("full_name", e.target.value)} data-testid="ca-full-name" />
                  </Field>
                  <Field label="Email Address" required>
                    <input className="field" type="email" value={form.email} onChange={(e) => update("email", e.target.value)} data-testid="ca-email" />
                  </Field>
                  <Field label="Telephone">
                    <input className="field" value={form.phone} onChange={(e) => update("phone", e.target.value)} data-testid="ca-phone" />
                  </Field>
                  <Field label="Current City/State">
                    <input className="field" value={form.location} onChange={(e) => update("location", e.target.value)} data-testid="ca-location" placeholder="Los Angeles, CA · relocating to Texas" />
                  </Field>
                </div>

                <Field label="You Are" required>
                  <div className="flex flex-wrap gap-2" data-testid="ca-role-group">
                    {ROLES.map((r) => (
                      <button
                        key={r.key}
                        type="button"
                        onClick={() => update("role", r.key)}
                        data-testid={`ca-role-${r.key}`}
                        className={`px-4 py-2 text-xs uppercase tracking-[0.2em] border transition ${
                          form.role === r.key
                            ? "bg-oxblood text-ivory border-oxblood"
                            : "bg-ivory border-transparent hover:border-oxblood"
                        }`}
                      >
                        {r.label}
                      </button>
                    ))}
                  </div>
                </Field>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <Field label="Manager / Agent / Referring Counsel (if any)">
                    <input
                      className="field"
                      value={form.firm_name}
                      onChange={(e) => update("firm_name", e.target.value)}
                      data-testid="ca-referral"
                      placeholder="Optional — we contact them only with your consent"
                    />
                  </Field>
                  <Field label="Approximate Annual Revenue Range">
                    <select
                      className="field"
                      value={form.estimated_value}
                      onChange={(e) => update("estimated_value", e.target.value)}
                      data-testid="ca-revenue-range"
                    >
                      <option value="">Select a range…</option>
                      {REVENUE_RANGES.map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </Field>
                </div>

                <Field label="Briefly describe what you'd like to discuss" required>
                  <textarea
                    className="field"
                    rows={7}
                    value={form.message}
                    onChange={(e) => update("message", e.target.value)}
                    data-testid="ca-message"
                    placeholder="Current entity structure, matters you'd like to address (contract review, entity restructure, tax planning, estate planning), and any timing considerations. No confidential details necessary at this stage — a general overview is sufficient."
                  />
                </Field>

                <Field label="Preferred Contact">
                  <select className="field" value={form.preferred_contact} onChange={(e) => update("preferred_contact", e.target.value)} data-testid="ca-preferred-contact">
                    <option value="email">By Email</option>
                    <option value="phone">By Telephone</option>
                    <option value="post">By Post</option>
                  </select>
                </Field>

                <div className="hairline pt-8 flex flex-wrap items-center justify-between gap-6">
                  <p className="text-xs text-slate2/70 font-cormorant italic max-w-md leading-snug">
                    Submitting this form does not create an attorney–client relationship. It is a
                    private inquiry, reviewed personally, and treated in strict confidence.
                  </p>
                  <button
                    type="submit"
                    disabled={submitting || !canSubmit}
                    className="btn-brass disabled:opacity-40 inline-flex items-center gap-2"
                    data-testid="ca-submit"
                  >
                    {submitting ? "Submitting…" : (<>Submit Privately <ArrowRight size={16} /></>)}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <div className="field-label mb-2">
        {label} {required && <span className="text-oxblood">·</span>}
      </div>
      {children}
    </label>
  );
}
